import React, { useContext, useRef, useState } from 'react';
import Table from 'react-bootstrap/Table';
import DeleteButton from '../Generic/DeleteButton';
import { deleteProduct } from '../../clients/product_client';
import ProductModal from './ProductModal';
import {
  CURRENCY_SYMBOLS,
  defaultImageUrl,
} from '../../config_and_helpers/config';
import ProductDetailModal from './ProductDetailModal';
import SupplierDetailModal from '../Supplier/SupplierDetailModal';
import ManufacturerDetailModal from '../Manufacturer/ManufacturerDetailModal';
import {
  formatDecimalNumber,
  getCurrencySymbol,
} from '../../config_and_helpers/helpers';
import DiscountedPriceComponent from '../Generic/DiscountedPriceComponent';
import Button from 'react-bootstrap/Button';
import NavigateToUrlIcon from '../Generic/NavigateToUrlIcon';
import { ProductContext } from '../../pages/Product/ProductList';
import { getProductDetailsOptimized } from '../../clients/product_client';
import { AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import SortableHeaderCell from '../Generic/SortableHeaderCell';
import { getProductAliquotsLocation } from '../../utils/productDisplay';
import { resolveOpenedLotCount } from '../../utils/openedLotCount';
import { getProcurementHighlightLevel, getProcurementRowBannerLabel } from '../../utils/productOpenProcurement';
import OpenedLotCount from './OpenedLotCount';
import './ProductComponentStyle.css';

/**
 * Represents a product table component.
 * @typedef {Object} ProductTable
 * @property {boolean} isSupplier - Indicates if the user is a supplier.
 * @property {Array} productList - The list of products.
 * @property {function} handleEdit - The function to handle product editing.
 * @property {function} clearSearchValue - The function to clear search value.
 * @property {boolean} similarProductsView - Indicates if the table is for similar products view.
 * @property {Array} similarProducts - The list of similar products.
 * @property {function} setSimilarProducts - The function to set similar products.
 */
const ProductTable = ({
  isSupplier,
  productList,
  handleEdit,
  clearSearchValue,
  similarProductsView,
  similarProducts = [],
  setSimilarProducts,
  sortBy = '',
  sortDirection = 'asc',
  onSort = null,
}) => {
  const { currencyRates } = useContext(ProductContext);
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();
  const showRowActions = !isSupplier && !isViewer;

  const getDisplayValue = value => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    return value;
  };

  const getSupplierModalProps = (fetchedSupplierObj, product) => {
    if (fetchedSupplierObj) {
      return { supplierObj: fetchedSupplierObj };
    }

    if (product?.supplier && typeof product.supplier === 'object') {
      return {
        supplierObj: product.supplier,
        supplierId: product.supplier.id,
      };
    }

    if (product?.supplier) {
      return { supplierObj: product.supplier, supplierId: product.supplier };
    }

    return { supplierObj: null };
  };

  const [fetchedSuppliers, setFetchedSuppliers] = useState([]); // State to store the suppliers being fetch to be used
  // thus preventing unnecessary requests
  const fetchedSuppliersRef = useRef(new Set()); // Ref storing the supplier Ids to be used to prevent requests

  // State to store products with full data for editing
  const [productsWithFullData, setProductsWithFullData] = useState({});
  const [loadingProducts, setLoadingProducts] = useState({});

  // Function handling the addition of a product to the similar products array
  const handleAddProduct = product => {
    setSimilarProducts(prevSimilarProducts => [
      ...prevSimilarProducts,
      {
        id: product.id,
        name: product.name,
        catNum: product.cat_num,
        imageUrl: product?.first_image || defaultImageUrl,
      },
    ]);
  };

  // Function to fetch full product data for editing
  const fetchFullProductData = async productId => {
    // Don't fetch if already loading or already fetched
    if (loadingProducts[productId] || productsWithFullData[productId]) {
      return;
    }

    setLoadingProducts(prev => ({ ...prev, [productId]: true }));

    try {
      const response = await getProductDetailsOptimized(
        token,
        productId,
        fullProductData => {
          setProductsWithFullData(prev => ({
            ...prev,
            [productId]: fullProductData,
          }));
        }
      );

      if (!response.success) {
        // Error handled silently
      }
    } catch (error) {
      // Error handled silently
    } finally {
      setLoadingProducts(prev => ({ ...prev, [productId]: false }));
    }
  };

  // Function to get the best available product data (full data if available, otherwise list data)
  // If productList has updated data (e.g., after edit), prefer it over cached full data
  const getProductDataForEditing = product => {
    const cachedFullData = productsWithFullData[product.id];

    // If we have cached full data, check if productList has more recent data (has images when cached doesn't)
    if (cachedFullData) {
      const productListHasImages = product.images && product.images.length > 0;
      const cachedHasImages =
        cachedFullData.images && cachedFullData.images.length > 0;

      // If productList has images but cached doesn't, use productList (it's more recent)
      if (productListHasImages && !cachedHasImages) {
        return product;
      }

      // Otherwise use cached full data
      return cachedFullData;
    }

    // No cached data, use product from list
    return product;
  };

  const inventoryListColumnExtras = !similarProductsView && !isSupplier ? 4 : 0;
  const productTableColumnCount =
    7 + inventoryListColumnExtras + 3 + (showRowActions ? 1 : 0);

  return (
    <Table striped bordered hover className="product-table--procurement-banners">
      <thead>
        <tr className="text-center">
          {/* The table header items */}
          {/* The '#' here stands for the numeric index of the product in the list */}
          <th>#</th>
          <th></th>
          <th>CAT #</th>
          <SortableHeaderCell
            label="Name"
            sortKey="name"
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          <SortableHeaderCell
            label="Category"
            sortKey="category"
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          <SortableHeaderCell
            label="Unit Quantity"
            sortKey="unit_quantity"
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
            style={{ minWidth: '130px' }}
          />
          <SortableHeaderCell
            label="Unit"
            sortKey="unit"
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          {!similarProductsView && !isSupplier
            ? (
              <SortableHeaderCell
                label="Stock"
                sortKey="stock"
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={onSort}
              />
            )
            : null}
          {!similarProductsView && !isSupplier
            ? (
              <th>Aliquots Location</th>
            )
            : null}
          {!similarProductsView && !isSupplier
            ? (
              <SortableHeaderCell
                label="Price"
                sortKey="price"
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={onSort}
              />
            )
            : null}

          <th>Product Profile</th>
          <SortableHeaderCell
            label="Manufacturer"
            sortKey="manufacturer_name"
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          <SortableHeaderCell
            label="Supplier"
            sortKey="supplier_name"
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          {showRowActions && !similarProductsView ? <th>Actions</th> : null}
        </tr>
      </thead>
      <tbody>
        {/* Mapping over the productList to create a table row for each product */}
        {productList.map((product, index) => {
          // Calculate total item stock for the current product
          // Use items array if available (from lazy loading), otherwise default to 0
          const totalItemStock =
            product.items && Array.isArray(product.items)
              ? product.items.reduce((total, item) => {
                  // Calculate stock based on item_sub_stock if available, otherwise use 1
                  const itemStock = item.item_sub_stock || 1;
                  return total + itemStock;
                }, 0)
              : 0;

          // Determine if the product is in the similar products array
          const isProductInSimilarProducts = similarProducts.some(
            similarProduct => similarProduct.id === product.id
          );

          // Fetch the supplier object, if exists
          const fetchedSupplierObj = fetchedSuppliers.find(
            supplier => supplier?.id === product.supplier
          );

          const procurementHighlight = getProcurementHighlightLevel(product);
          const procurementBannerLabel = getProcurementRowBannerLabel(procurementHighlight);

          return (
            <React.Fragment key={product.id}>
            <tr
              className={[
                'text-center align-middle',
                procurementHighlight === 'quote' && 'product-row-procurement-quote',
                procurementHighlight === 'order' && 'product-row-procurement-order',
              ]
                .filter(Boolean)
                .join(' ')}
              style={{ fontSize: '14px' }}
            >
              {/* Displays the serial number of the item */}
              <td>{index + 1}</td>
              {/* Displays the product image, if it exists, or a default image */}
              <td>
                <a
                  href={product?.first_image || defaultImageUrl}
                  className="link-"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    style={{ width: '150px', height: '150px' }}
                    src={product?.first_image || defaultImageUrl}
                    alt={product.name || 'Product'}
                  />
                </a>
              </td>
              <td>{product.cat_num}</td>
              <td>
                {/* Renders the product detail modal for each product */}
                <ProductDetailModal
                  isSupplier={isSupplier}
                  productObj={product}
                  updateProducts={handleEdit}
                  clearSearchValue={clearSearchValue}
                />
              </td>
              <td>{getDisplayValue(product?.category)}</td>
              <td>
                {product?.unit_quantity !== null &&
                product?.unit_quantity !== undefined
                  ? formatDecimalNumber(product.unit_quantity)
                  : '-'}
              </td>
              <td>
                {product?.unit === 'Assays'
                  ? 'Reactions / Tests / Assays'
                  : getDisplayValue(product?.unit)}
              </td>
              {!similarProductsView && !isSupplier ? (
                <td>
                  {
                    /* Display stock information. If items are available, show calculated stock from items */
                    product.items &&
                    Array.isArray(product.items) &&
                    product.items.length > 0
                      ? `${totalItemStock} ${
                          product.unit === 'Package' || product.unit === 'Box'
                            ? `(from ${product.items.length} items)`
                            : ''
                        }`
                      : product.stock !== null
                      ? `${product.stock} ${
                          product.unit === 'Package' || product.unit === 'Box'
                            ? `(${totalItemStock})`
                            : ''
                        }`
                      : '-'
                  }
                  <OpenedLotCount count={resolveOpenedLotCount(product)} />
                </td>
              ) : null}
              {!similarProductsView && !isSupplier ? (
                <td>{getDisplayValue(getProductAliquotsLocation(product))}</td>
              ) : null}
              {!similarProductsView && !isSupplier ? (
                product && product.discount === null ? (
                  <td style={{ minWidth: '100px' }}>
                    {product.price !== null
                      ? `${formatDecimalNumber(product.price)}${
                          CURRENCY_SYMBOLS[product.currency]
                            ? ` ${getCurrencySymbol(product.currency)}`
                            : ''
                        }`
                      : ''}
                  </td>
                ) : product ? (
                  <td>
                    <div>
                      <span>
                        Pre-Discount: {formatDecimalNumber(product.price)}
                        {CURRENCY_SYMBOLS[product.currency]
                          ? `${getCurrencySymbol(product.currency)}`
                          : ''}
                      </span>
                    </div>
                    <div>
                      <span>
                        <DiscountedPriceComponent
                          product={product}
                          currencyRates={currencyRates}
                        />
                        {CURRENCY_SYMBOLS[product.currency]
                          ? `${getCurrencySymbol(product.currency)}`
                          : ''}{' '}
                        (-{formatDecimalNumber(product.discount)}%)
                      </span>
                    </div>
                  </td>
                ) : null
              ) : null}

              <td>
                {product.url && (
                  <a
                    href={product.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <NavigateToUrlIcon />
                  </a>
                )}
                {!product?.url ? '-' : null}
              </td>
              <td>
                {/* Renders the manufacturer detail modal for each product */}
                {product?.manufacturer && typeof product.manufacturer === 'object' && (
                  <ManufacturerDetailModal
                    isSupplier={isSupplier}
                    manufacturerObj={product.manufacturer}
                    smallerFont={true}
                  />
                )}
                {!product?.manufacturer && getDisplayValue(product?.manufacturer_name)}
                {product?.manufacturer &&
                typeof product.manufacturer !== 'object' &&
                  getDisplayValue(product?.manufacturer_name)}
              </td>
              <td>
                {/* Renders the supplier detail modal for each product */}
                <SupplierDetailModal
                  isSupplier={isSupplier}
                  fetchingSuppliersRef={fetchedSuppliersRef}
                  fetchedSuppliers={fetchedSuppliers}
                  setFetchedSuppliers={setFetchedSuppliers}
                  {...getSupplierModalProps(fetchedSupplierObj, product)}
                  smallerFont={true}
                />
              </td>
              {/* Renders an edit button and a delete button for each product */}
              {showRowActions && (
                <td className="align-items-center">
                  {!similarProductsView ? (
                    <>
                      <div className="mb-2">
                        <ProductModal
                          productObj={getProductDataForEditing(product)}
                          clearSearchValue={clearSearchValue}
                          onEditButtonHover={() =>
                            fetchFullProductData(product.id)
                          }
                          isEditButtonLoading={loadingProducts[product.id]}
                        />
                      </div>
                      <DeleteButton
                        objectType="product"
                        objectName={product.name}
                        objectId={product.id}
                        deleteFetchFunc={deleteProduct}
                        onSuccessfulDelete={handleEdit}
                        clearSearchValue={clearSearchValue}
                      />
                    </>
                  ) : (
                    <Button
                      onClick={() => handleAddProduct(product)}
                      disabled={isProductInSimilarProducts}
                    >
                      {isProductInSimilarProducts
                        ? 'Product Added!'
                        : 'Add Product'}
                    </Button>
                  )}
                </td>
              )}
            </tr>
            {procurementBannerLabel && (
              <tr
                className={[
                  'product-row-procurement-banner',
                  procurementHighlight === 'quote' &&
                    'product-row-procurement-banner--quote',
                  procurementHighlight === 'order' &&
                    'product-row-procurement-banner--order',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <td
                  colSpan={productTableColumnCount}
                  className="product-row-procurement-banner__anchor"
                >
                  <div className="product-row-procurement-banner__pill">
                    {procurementBannerLabel}
                  </div>
                </td>
              </tr>
            )}
            </React.Fragment>
          );
        })}
      </tbody>
    </Table>
  );
};
export default ProductTable;
